'use client';

import {
  Avatar,
  Stack,
  Text,
  HStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Icon,
} from '@chakra-ui/react';
import { Link } from '@chakra-ui/next-js';
import { ROUTES } from '@habity/constants';
import { ICONS } from '~/constants/icons';
import { useUsers } from '~/hooks/useUsers';
import { useAgency } from '~/hooks/useAgency';
import { getFullName, getSellPath } from '@habity/services';
import { useTranslations } from 'next-intl';
import { useUser } from '~/hooks/useUser';
import { useUserData } from '~/hooks/useUserData';
import { PlasmicCanvasContext } from '@plasmicapp/loader-nextjs';
import { useContext } from 'react';

import { useUIStore } from '~/store/useUIStore';
import { useAppStore } from '~/store/useAppStore';
import { MdAccountCircle } from 'react-icons/md';

interface DesktopMenuProps {
  isLoggedIn?: boolean;
  onClose?: (...args: any) => void;
}

export function DesktopMenu({ isLoggedIn, onClose }: DesktopMenuProps) {
  const { signOut } = useUsers();
  const { agency } = useAgency();
  const t = useTranslations('Menu');
  const { user: appUser } = useUser();
  // Use SWR-based user data for real-time count updates
  const swrUser = useUserData();
  const user = swrUser || appUser;
  const openLoginModal = useUIStore((state) => state.openLoginModal);
  const { homes, searches, interests } = user || {};
  const wishlistItemsCount =
    interests?.filter((i) => i.interest_level === 'added_to_wishlist').length ||
    0;
  const interestsCount =
    interests?.filter((i) => i.interest_level === 'showed_interest').length ||
    0;
  const viewingsCount =
    interests?.filter((i) => i.interest_level === 'booked_viewing').length || 0;
  const modals = useUIStore((state) => state.modals);
  const setModals = useUIStore((state) => state.setModals);
  const isMobile = useAppStore((state) => state.isMobile);

  const inPlasmicEditor = Boolean(useContext(PlasmicCanvasContext));

  // Login state can be passed in
  // One use case is from plasmic for editor "preview"
  const loggedIn = inPlasmicEditor ? isLoggedIn : Boolean(user);

  const logOut = async () => {
    await signOut();
    if (onClose) {
      onClose();
    }
  };

  const handleOpenClientPortal = () => {
    // On mobile, redirect directly to external site (BankID doesn't work in iframes)
    if (isMobile) {
      window.location.href = 'https://minasidor.erikolsson.se';
    } else {
      // On desktop, open drawer
      setModals({ ...modals, clientPortalOpen: true });
      onClose?.();
    }
  };

  return (
    <Stack
      fontSize="sm"
      pb="0"
      borderWidth="0"
      minWidth="280px"
      backgroundColor="white"
      gap="0"
      flex="1"
      justifyContent="space-between"
    >
      <Stack gap="0" justifyContent="flex-start" flex="1" pb="4">
        {loggedIn && (
          <>
            <Stack spacing="1" pt="4" px="4" pb="2">
              <HStack spacing="4" alignItems="center">
                <Avatar
                  boxSize="12"
                  name={getFullName(user)}
                  backgroundColor="brand.500"
                  color="white"
                  fontWeight="medium"
                  src={user?.avatar_url || undefined}
                />
                <Stack gap="0">
                  <Text
                    size="lg"
                    color="black"
                    fontWeight="medium"
                    letterSpacing="-1px"
                  >
                    {getFullName(user)}
                  </Text>
                  <Link href={ROUTES.DASHBOARD.ACCOUNT}>
                    <HStack
                      color="blackAlpha.700"
                      spacing="1"
                      onClick={onClose}
                    >
                      <Text size="xs">Gå till min profil</Text>
                      <ICONS.NEXT />
                    </HStack>
                  </Link>
                </Stack>
              </HStack>
            </Stack>
          </>
        )}

        {/* Tabs and links */}
        <Stack>
          {loggedIn && (
            <Tabs colorScheme="black">
              <TabList
                w="100%"
                justifyContent="space-evenly"
                borderBottomWidth="0px"
                px="4"
              >
                <Tab fontSize="xs" flex="1">
                  Säljare
                </Tab>
                <Tab fontSize="xs" flex="1">
                  Spekulant
                </Tab>
              </TabList>
              <TabPanels>
                <TabPanel>
                  <Stack spacing="4">
                    {/* Links */}
                    <Stack fontSize="sm" gap="0">
                      {loggedIn && (
                        <HStack
                          color="blackAlpha.900"
                          onClick={handleOpenClientPortal}
                          py="1.5"
                          px="2"
                          _hover={{ backgroundColor: 'blackAlpha.100' }}
                          cursor="pointer"
                        >
                          <Icon as={MdAccountCircle} size={18} />
                          <Text>Mina sidor</Text>
                        </HStack>
                      )}

                      {!loggedIn && (
                        <HStack
                          color="blackAlpha.900"
                          onClick={() => {
                            onClose?.();
                            openLoginModal();
                          }}
                          py="1.5"
                          px="2"
                          _hover={{ backgroundColor: 'blackAlpha.100' }}
                        >
                          <Icon as={ICONS.SELL} size={18} />
                          <Text>Logga in</Text>
                        </HStack>
                      )}

                      {homes && homes?.length > 0 && (
                        <Link href={ROUTES.DASHBOARD.SELLER.INDEX}>
                          <HStack
                            color="blackAlpha.900"
                            py="1.5"
                            px="2"
                            _hover={{ backgroundColor: 'blackAlpha.100' }}
                            onClick={onClose}
                          >
                            <Icon as={ICONS.MY_HOMES} size={18} />
                            <Text>Mina bostäder ({homes?.length})</Text>
                          </HStack>
                        </Link>
                      )}

                      <Link href={getSellPath(undefined, 'valuation')}>
                        <HStack
                          color="blackAlpha.900"
                          py="1.5"
                          px="2"
                          _hover={{ backgroundColor: 'blackAlpha.100' }}
                          onClick={onClose}
                        >
                          <Icon as={ICONS.SELL} size={18} />
                          <Text>Värdera bostad</Text>
                        </HStack>
                      </Link>

                      <Link href={ROUTES.SEARCH.BROKERS}>
                        <HStack
                          color="blackAlpha.900"
                          py="1.5"
                          px="2"
                          _hover={{ backgroundColor: 'blackAlpha.100' }}
                          onClick={onClose}
                        >
                          <Icon as={ICONS.BROKER} size={18} />
                          <Text>Hitta mäklare</Text>
                        </HStack>
                      </Link>
                    </Stack>
                  </Stack>
                </TabPanel>
                <TabPanel>
                  <Stack spacing="4">
                    <Stack fontSize="sm" gap="0">
                      {!loggedIn && (
                        <HStack
                          color="blackAlpha.900"
                          onClick={() => {
                            onClose?.();
                            openLoginModal();
                          }}
                          py="1.5"
                          px="2"
                          _hover={{ backgroundColor: 'blackAlpha.100' }}
                        >
                          <Icon as={ICONS.SELL} size={18} />
                          <Text>Logga in</Text>
                        </HStack>
                      )}

                      {searches && searches.length > 0 && (
                        <Link href={ROUTES.DASHBOARD.BUYER.INDEX}>
                          <HStack
                            color="blackAlpha.900"
                            py="1.5"
                            px="2"
                            _hover={{ backgroundColor: 'blackAlpha.100' }}
                            onClick={onClose}
                          >
                            <Icon as={ICONS.SEARCHES} size={18} />
                            <Text>Bevakningar ({searches?.length})</Text>
                          </HStack>
                        </Link>
                      )}

                      {wishlistItemsCount > 0 && (
                        <Link
                          href={`${ROUTES.DASHBOARD.BUYER.INDEX}?tab=wishlists`}
                        >
                          <HStack
                            color="blackAlpha.900"
                            py="1.5"
                            px="2"
                            _hover={{ backgroundColor: 'blackAlpha.100' }}
                            onClick={onClose}
                          >
                            <Icon as={ICONS.WISHLIST} size={18} />
                            <Text>Favoriter ({wishlistItemsCount})</Text>
                          </HStack>
                        </Link>
                      )}

                      {interestsCount > 0 && (
                        <Link
                          href={`${ROUTES.DASHBOARD.BUYER.INDEX}?tab=interests`}
                        >
                          <HStack
                            color="blackAlpha.900"
                            py="1.5"
                            px="2"
                            _hover={{ backgroundColor: 'blackAlpha.100' }}
                            onClick={onClose}
                          >
                            <Icon as={ICONS.INTEREST} size={18} />
                            <Text>Intresseanmälda ({interestsCount})</Text>
                          </HStack>
                        </Link>
                      )}

                      {viewingsCount > 0 && (
                        <Link
                          href={`${ROUTES.DASHBOARD.BUYER.INDEX}?tab=viewings`}
                        >
                          <HStack
                            color="blackAlpha.900"
                            py="1.5"
                            px="2"
                            _hover={{ backgroundColor: 'blackAlpha.100' }}
                            onClick={onClose}
                          >
                            <Icon as={ICONS.CALENDAR_CHECK} size={18} />
                            <Text>Visningar ({viewingsCount})</Text>
                          </HStack>
                        </Link>
                      )}

                      <Link href={ROUTES.SEARCH.HOMES}>
                        <HStack
                          color="blackAlpha.900"
                          py="1.5"
                          px="2"
                          _hover={{ backgroundColor: 'blackAlpha.100' }}
                          onClick={onClose}
                        >
                          <Icon as={ICONS.SEARCH} size={18} />
                          <Text>Hitta bostad</Text>
                        </HStack>
                      </Link>

                      <Link href={ROUTES.BUY}>
                        <HStack
                          color="blackAlpha.900"
                          py="1.5"
                          px="2"
                          _hover={{ backgroundColor: 'blackAlpha.100' }}
                          onClick={onClose}
                        >
                          <Icon as={ICONS.ADD_HOMEBUYERS} size={18} />
                          <Text>Skapa bevakning</Text>
                        </HStack>
                      </Link>
                    </Stack>
                  </Stack>
                </TabPanel>
              </TabPanels>
            </Tabs>
          )}
        </Stack>
      </Stack>

      <Stack gap={0}>
        <Stack
          backgroundColor="black"
          color="white"
          py="6"
          px="6"
          spacing="0"
          gap="1.5"
          fontSize="sm"
        >
          <Link href={`mailto:${agency?.support_email}`}>
            <Text
              color="white"
              backgroundColor="black"
              _hover={{ textDecoration: 'underline' }} // Consistency with other links
              onClick={onClose}
            >
              Hjälp & Support
            </Text>
          </Link>
          {loggedIn && (
            <Text
              color="white"
              backgroundColor="black"
              _hover={{ textDecoration: 'underline' }} // Consistency with other links
              onClick={logOut}
              cursor="pointer"
            >
              Logga ut
            </Text>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
}
